import CustomDialog from '@/components/muiComponents/alert/dialog';
import { useAuth } from '@/hooks/useAuth';
import { shouldUseDemoAuth } from '@/util/demoUtils';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TIMEOUT_INTERVAL =
  Number(process.env.REACT_APP_SESSION_TIMEOUT_DURATION_IN_MIN) * 60 * 1000; // 10 minutes
const WARNING_INTERVAL =
  Number(process.env.REACT_APP_SESSION_WARNING_TIME_IN_MIN) * 60 * 1000; // 1 minute before timeout
const CHANNEL_NAME = process.env.REACT_APP_SESSION_CHANNEL_NAME || '';
const WARNING_INTERVAL_SEC = WARNING_INTERVAL / 1000;
const withInactivitySession = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
) => {
  return (props: P) => {
    const { authState, logout } = useAuth(); // Check authentication status
    const isAuthenticated = authState?.isAuthenticated;
    const { t } = useTranslation(['auth']);
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
    const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
    const broadcast = useRef<BroadcastChannel | null>(null);
    const [showWarning, setShowWarning] = useState(false);
    const [remainingSeconds, setRemainingSeconds] =
      useState(WARNING_INTERVAL_SEC);

    const resetTimer = useCallback(() => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      setShowWarning(false);
      setRemainingSeconds(WARNING_INTERVAL_SEC);

      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        startCountdown();
      }, TIMEOUT_INTERVAL - WARNING_INTERVAL);

      inactivityTimerRef.current = setTimeout(() => {
        if (broadcast.current) {
          broadcast.current.postMessage('logout');
        }
        // In demo environment, session timeout should show demo login
        if (shouldUseDemoAuth()) {
          // Clear auth state and reload to show demo login
          window.location.reload();
        } else {
          logout();
        }
      }, TIMEOUT_INTERVAL);
    }, [logout]);

    const startCountdown = () => {
      countdownTimerRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const handleActivity = useCallback(() => {
      if (broadcast.current) {
        broadcast.current.postMessage('activity');
      }
      resetTimer();
    }, [resetTimer]);

    const handleMessage = useCallback(
      (event: MessageEvent) => {
        if (event.data === 'activity') {
          resetTimer();
        } else if (event.data === 'logout') {
          // Handle logout message from other tabs
          if (shouldUseDemoAuth()) {
            window.location.reload();
          } else {
            logout();
          }
        }
      },
      [logout, resetTimer],
    );

    useEffect(() => {
      if (!isAuthenticated) return; // Skip session management if user is not authenticated

      if (typeof window !== 'undefined') {
        broadcast.current = new BroadcastChannel(CHANNEL_NAME);
        broadcast.current.onmessage = handleMessage;
        resetTimer();
      }

      return () => {
        if (broadcast.current) {
          broadcast.current.close();
          broadcast.current = null;
        }
        if (inactivityTimerRef.current)
          clearTimeout(inactivityTimerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      };
    }, [isAuthenticated, handleMessage, resetTimer]);

    // If user is not authenticated, return the component without inactivity session logic
    if (!isAuthenticated) {
      return <WrappedComponent {...props} />;
    }

    return (
      <>
        <div
          data-testid="inactivity-session-wrapper"
          onMouseMove={handleActivity}
          onTouchStart={handleActivity}
          onKeyDown={handleActivity}
          onClick={handleActivity}
          onScroll={handleActivity}
        >
          <WrappedComponent {...props} />
        </div>
        <CustomDialog
          open={showWarning}
          handleClose={handleActivity}
          title={t('warningDialogTitle')}
          data-testid="session-timeout-dialog"
        >
          <Typography data-testid="session-timeout-message">
            {t('warningMessage', {
              remainingSeconds:
                WARNING_INTERVAL_SEC === remainingSeconds
                  ? ''
                  : remainingSeconds,
            })}
          </Typography>
        </CustomDialog>
      </>
    );
  };
};

export default withInactivitySession;
